import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@luparx/i18n';
import { ActiveTenantBadge, TenantSheet, useIsOnline } from '@luparx/features';
import {
  AppBar,
  Badge,
  Brand,
  BottomTabBar,
  IconBell,
  IconCar,
  IconHome,
  IconList,
  IconPark,
  IconWallet,
  SelloDeCompilacion,
} from '@luparx/ui';
import type { BottomTab } from '@luparx/ui';
import { ActiveSessionsBar } from './ActiveSessionsBar';
import { useUnreadNotificationCount } from '../lib/queries';
import { useTecladoVirtual } from '../lib/tecladoVirtual';

export interface CitizenShellProps {
  children: React.ReactNode;
  /** Detail-screen title, shown in the compact app bar next to a back arrow (DESIGN_SYSTEM.md §3 "App bar"). Requires `onBack`. */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  onBack?: () => void;
  /**
   * Una pantalla raíz de pestaña SIN barra superior: su propio título de pantalla ocupa el lugar.
   *
   * <p>Era lo que usaban Vehículos, Billetera y Más hasta el 06-10-2026, cuando se reportó que esas
   * tres no mostraban la cabecera que el resto de la experiencia sí tiene. Las tres pasaron a
   * llevarla; `bare` se conserva porque Multas y Mi cuenta siguen usándolo y porque la decisión de
   * qué pantallas la llevan es de quien diseña, no de este componente.</p>
   */
  bare?: boolean;
  /**
   * El título de la pantalla y lo que va en su línea, como el botón «Agregar» de Vehículos.
   *
   * <p>Es una prop y no el primer hijo porque en una pantalla `bare` la barra de estadía en curso va
   * ENTRE el título y el contenido: ahí el título es lo que dice dónde estás, y empujarlo debajo de
   * una barra que sólo está a veces hacía que la pantalla pareciera pertenecer al cronómetro.</p>
   *
   * <p>Con barra superior el orden es el natural —cromo fijo, después el título, después el
   * contenido— y el título se desplaza con el contenido, que es donde pertenece: lo que se queda
   * arriba es la barra.</p>
   */
  heading?: React.ReactNode;
}

/**
 * Shared chrome for every citizen screen: brand+bell app bar (Home), no app
 * bar at all (`bare` — tab-root list screens render their own in-content
 * heading), or back+title(+subtitle) (drill-down screens) — plus the fixed
 * 5-destination bottom bar (DESIGN_SYSTEM.md §3).
 */
export function CitizenShell({
  children,
  title,
  subtitle,
  onBack,
  bare = false,
  heading,
}: CitizenShellProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /*
    El teclado del teléfono, en el armazón y no en una pantalla suelta: todos los formularios del
    portal viven dentro de este contenedor, y el problema —la navegación inferior y el botón fijo
    tapando el campo que se está escribiendo— es el mismo en el código de bahía, en el registro y
    en el perfil. Un solo sitio que lo detecte, una sola regla de estilos que reaccione.
  */
  useTecladoVirtual();
  // Only for the root screens that show the bell: a detail screen renders a back arrow instead, and
  // polling a count nobody can see would be a request per minute per open screen for nothing.
  const unreadQuery = useUnreadNotificationCount();
  /*
    El MISMO dato que la fila «Estado de conexión» de Más lee desde el 19-09-2026: `useIsOnline` de
    `@luparx/features`, que escucha los eventos `online`/`offline` del navegador. No se creó lógica
    ni API nueva —el PDF del 06-10-2026 lo prohíbe en letra— y no hace falta: el hook ya estaba
    importado en este mismo portal, a dos pantallas de acá.
  */
  const enLinea = useIsOnline();
  const unreadCount = unreadQuery.data?.unread;
  const location = useLocation();
  /** La única pantalla sin campana: la de los avisos. Un botón hacia donde ya estás. */
  const enAvisos = location.pathname.startsWith('/notifications');

  /*
    Al cambiar de pantalla, arriba (09-10-2026).

    El mismo defecto que el fiscalizador tenía el 08-10 y que se corrigió entonces en su shell:
    con la pantalla desplazada al fondo y tocando un destino de la barra inferior, la pantalla
    nueva aparecía a media altura —acá `scrollY=221`—, con su título fuera de la vista. React
    Router no reposiciona el desplazamiento al navegar y el navegador conserva el que traía.

    Que apareciera un día después no es casualidad ni una regresión de la fachada: el 08-10 quedó
    anotado que el ciudadano tenía el mismo defecto latente y que sólo faltaba que su Inicio
    creciera lo suficiente para que el desplazamiento heredado fuese una posición válida. El
    héroe lo hizo crecer.

    Por `pathname` y no por la ubicación completa: cambiar un parámetro de búsqueda en la misma
    pantalla no es llegar a otra parte y no debería mover la vista.
  */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // The footer (the tab bar) is `position: fixed`, so it never reserves space in normal flow on
  // its own — measured here and applied as `main`'s bottom padding so it can never cover page
  // content. A plain `position: sticky` footer looked equivalent but only "sticks" once its static
  // flow position would scroll past the viewport edge; on a page taller than one screen it stayed
  // glued to the bottom from scroll position zero and overlapped whatever content happened to
  // render underneath — fixed + measured padding is what actually holds on every screen, tall or
  // short. The top chrome needs none of that: sticky is exactly right at the top, because the
  // element's static position *is* where it belongs before any scrolling, so it reserves its own
  // space in flow and can never cover anything.
  // The municipality sheet is opened from the top-bar badge and closed again in place. It is not a
  // route: the person is in the middle of a screen, and navigating away and back would lose
  // whatever they had scrolled to or typed for the sake of a choice that takes one tap.
  const [tenantSheetOpen, setTenantSheetOpen] = useState(false);

  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  useEffect(() => {
    const node = footerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      /*
        `borderBoxSize` y NO `contentRect`: éste devuelve la caja de CONTENIDO, sin el padding, y la
        barra de pestañas paga `padding-bottom: env(safe-area-inset-bottom)` — la franja del gesto
        del iPhone. En un 14 Pro Max eso son 34px que la barra ocupa y que `contentRect` no reporta:
        medido, 90px reales contra 56 informados.

        El efecto era que `main` reservaba 34px de menos y lo ÚLTIMO de cada pantalla quedaba debajo
        de la barra: la tarjeta de multas y «Ver todas» en el Inicio, «Agregar tarjeta» en la
        Billetera, el selector de idioma en Más. Detectado por
        tests/responsive/sobre-el-pliegue.cjs el 2026-09-19.

        `getBoundingClientRect()` de respaldo para navegadores sin `borderBoxSize` (Safari < 15.4),
        que devuelve lo mismo y está siempre disponible.
      */
      const alto = entry.borderBoxSize?.[0]?.blockSize ?? node.getBoundingClientRect().height;
      setFooterHeight(alto);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const tabs: BottomTab[] = [
    {
      key: 'home',
      label: t('nav.home'),
      icon: <IconHome />,
      onSelect: () => navigate('/'),
      current: location.pathname === '/',
    },
    {
      key: 'park',
      label: t('nav.park'),
      icon: <IconPark />,
      onSelect: () => navigate('/park'),
      current: location.pathname.startsWith('/park'),
    },
    {
      key: 'vehicles',
      label: t('nav.vehicles'),
      icon: <IconCar />,
      onSelect: () => navigate('/vehicles'),
      current: location.pathname === '/vehicles',
    },
    {
      key: 'wallet',
      label: t('nav.wallet'),
      icon: <IconWallet />,
      onSelect: () => navigate('/wallet'),
      current: location.pathname === '/wallet' || location.pathname === '/movements',
    },
    {
      key: 'more',
      label: t('nav.more'),
      icon: <IconList />,
      onSelect: () => navigate('/more'),
      current:
        location.pathname === '/more' ||
        location.pathname.startsWith('/profile') ||
        location.pathname.startsWith('/fines'),
    },
  ];

  return (
    // `100dvh` y no `100vh`: en Safari de iPhone `vh` es la altura con la barra de herramientas
    // RETRAÍDA, o sea más alta que lo que se ve. El shell se declaraba más alto que la pantalla, la
    // barra de pestañas quedaba empujada fuera del área visible y había que hacer scroll para
    // alcanzar acciones que están arriba del todo —reportado en un iPhone 14 Pro Max al buscar
    // «agregar vehículo», que vive en la cabecera. `dvh` sigue a la barra en vez de ignorarla.
    <div
      // La altura REAL de la barra de pestañas, medida acá y publicada como variable para que un
      // CTA fijo pueda apoyarse en ella sin volver a medirla ni codificar un número: la barra
      // cambia de alto con la safe-area del aparato y con el modo de accesibilidad.
      style={
        {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          background: 'transparent',
          '--lx-bottom-nav-height': `${footerHeight}px`,
        } as React.CSSProperties
      }
    >
      {/* On a `bare` screen the heading scrolls away like ordinary content and the timer bar below
          it is what stays — so the screen is titled by its own name, not by whatever is parked
          (CONTRACT.md v0.10). It sits outside `main` only so the bar can come between the two. */}
      {bare && heading ? <div className="lx-screen-heading">{heading}</div> : null}
      {/* Top chrome as one sticky group: the app bar (when the screen has one) and, on every screen
          while a stay is running, the countdown (CONTRACT.md v0.2 rule 3 / v0.10). Grouping them is
          what keeps the safe-area inset handled exactly once — the app bar already pays it, and on
          `bare` screens the timer bar pays it instead as the group's only child (see
          `.lx-top-chrome > .lx-sticky-timer-bar:first-child` in tokens.css). */}
      <div className="lx-top-chrome">
        {bare ? null : (
        <AppBar
          className="lx-app-bar--citizen"
          start={
            !onBack ? (
              <>
                <Brand name={t('app.name')} />
                {/* The active municipality lives beside the LuParX mark (CONTRACT.md v0.4) and is
                    the way back to the picker. It renders nothing until one is chosen, and is only
                    pressable when the account has more than one to choose between. */}
                <ActiveTenantBadge onOpenSelector={() => setTenantSheetOpen(true)} />
              </>
            ) : undefined
          }
          onBack={onBack}
          backLabel={t('common.back')}
          title={onBack ? title : undefined}
          subtitle={onBack ? subtitle : undefined}
          /*
            El estado de conexión, arriba a la derecha de la MISMA fila (06-10-2026).

            Sólo en las pantallas raíz —las que llevan marca, municipalidad y campana—, que son las
            cinco de la barra inferior más Movimientos y Notificaciones. Una pantalla de detalle
            lleva flecha y título, y meterle una insignia de conexión al lado del título sería
            cambiar pantallas que el PDF deja explícitamente fuera.

            `end` y no `actions`: `actions` dibuja `<button>` con un icono dentro, y esto no se
            pulsa. Va ANTES de la campana porque el orden de lectura deja lo accionable en la
            esquina, donde está el pulgar.

            Pequeño y discreto, que es lo que pide el PDF: sin icono y un punto más chico que una
            insignia de contenido. En esta fila ya hay marca, municipalidad y campana, y a 320px el
            icono de 16px era la diferencia entre caber y no caber. El tono no viaja solo: lleva la
            palabra, siempre.
          */
          end={
            !onBack ? (
              <Badge
                className="lx-connection-badge lx-connection-badge--compact"
                tone={enLinea ? 'success' : 'warning'}
              >
                {enLinea ? t('citizen.more.connection.ok') : t('citizen.more.connection.none')}
              </Badge>
            ) : undefined
          }
          actions={
            /*
              La campana se queda en TODAS las pantallas menos en la de avisos (07-10-2026).

              Era `!onBack`: sólo en las raíces. El mismo defecto que el fiscalizador tenía y que el
              PDF del 07-10 reportó allá con una captura — entrar a una pantalla interna hacía
              desaparecer la campana y, con ella, el contador de avisos sin leer. Un indicador que se
              esconde al navegar no es un indicador: su trabajo es estar cuando uno NO lo está
              buscando.

              Se corrige acá aunque el encargo del ciudadano hablara sólo de la cabecera de Ayuda,
              porque el encargo pide «mantener la experiencia visual consistente con las demás
              pantallas» y porque devolverle la cabecera a Ayuda SIN esto le habría quitado la
              campana a esa pantalla: habría cambiado un defecto por otro.

              La única excepción es la propia pantalla de avisos: ahí sería un botón hacia donde ya
              estás. Se decide por la ruta y no por `onBack`, que contesta otra pregunta.
            */
            enAvisos
              ? []
              : [
                  {
                    icon: <IconBell />,
                    label: t('common.notifications'),
                    onClick: () => navigate('/notifications'),
                    // Undefined and not 0 while the count is still loading, so the badge is absent
                    // rather than briefly claiming "0" — AppBar hides a falsy count either way, but
                    // saying "no news" before asking would be the screen guessing.
                    badgeCount: unreadCount,
                  },
                ]
          }
        />
        )}
        <ActiveSessionsBar />
      </div>
      {/* Con barra superior el título va DEBAJO del cromo fijo, no encima: encima quedaría tapado
          por la barra en cuanto se desplace, que es el defecto que esto viene a no introducir. */}
      {!bare && heading ? (
        <div className="lx-screen-heading lx-screen-heading--under-bar">{heading}</div>
      ) : null}
      <main
        style={{
          flex: 1,
          paddingTop: 'var(--lx-space-4)',
          paddingRight: 'var(--lx-space-4)',
          paddingLeft: 'var(--lx-space-4)',
          /*
            El piso de 100px (09-10-2026).

            La fachada lo pide por su número —«main con 100-115 px de padding-bottom»— y abre ese
            punto diciendo «no repetir el problema de Fiscalización». Con la barra en ~72 y 16 de
            respiro salían 88: bastaba para no tapar y no llegaba a lo pedido.

            `max` y no un número fijo: en un aparato con safe-area la barra mide más y la medición
            manda; sin ella, manda el piso. No se sustituye una medida real por una constante, se
            le pone suelo. Es el mismo arreglo que el fiscalizador lleva desde ayer.
          */
          /*
            Y el teclado, cuando está arriba (20-10-2026).

            Va SUMADO acá y no en una regla de la hoja de estilos porque este relleno es un estilo
            EN LÍNEA, y un estilo en línea le gana a cualquier selector: la regla que escribí
            primero en `tokens.css` no pintaba nada y el campo seguía sin caber. Dos sitios
            decidiendo el mismo relleno es el error; éste es el sitio.

            Sin teclado, `--lx-teclado-alto` no existe, el sumando vale 0 y el relleno es
            exactamente el de antes. Con teclado, la página crece lo que el teclado ocupa, que es
            lo que permite que el último campo del formulario suba hasta quedar a la vista: medido
            el 20-10 a 320×568, sin esto la página ya estaba en su tope de scroll con el campo por
            debajo del borde, y ningún desplazamiento podía arreglarlo porque no había a dónde ir.
          */
          paddingBottom: `calc(max(100px, calc(var(--lx-space-4) + ${footerHeight}px)) + var(--lx-teclado-alto, 0px))`,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--lx-card-gap)',
          maxWidth: 560,
          width: '100%',
          margin: '0 auto',
        }}
      >
        {children}
      {/*
        El sello de compilación. Va al final del contenido y no pegado abajo: pegado tendría que
        pelear con la barra de navegación, con la barra fija de «Continuar» y con el teclado, y
        acabaría tapando justo la pantalla que viene a verificar. En producción no se dibuja.
      */}
      <SelloDeCompilacion
        entorno={__LUPARX_ENV__}
        construidoEn={__LUPARX_BUILT_AT__}
        commit={__LUPARX_BUILD__}
      />
      </main>
      {/*
        `lx-bottom-dock`: el enganche que la hoja de estilos necesita para subir la navegación por
        encima del teclado en vez de esconderla (23-10-2026). La clase va en el contenedor FIJO y
        no en la barra de dentro, porque lo que hay que mover es lo que está pegado al borde; la
        regla anterior ocultaba la barra interior, que es otra cosa y por eso bastaba con tocarla.

        Y la colocación se fue de aquí a la hoja de estilos, que no es cosmética: estaba en
        `style={{ position: 'fixed', ... }}` y un estilo en línea gana a cualquier regla sin
        `!important`. La regla que suelta esta barra cuando el campo no cabe se escribió, se
        aplicó… y no hizo nada, porque el `position: fixed` de aquí la pisaba. Medido a 844×390:
        `data-barras='nav-suelta'` puesto y la barra seguía `fixed`.
      */}
      <div ref={footerRef} className="lx-bottom-dock">
        <BottomTabBar tabs={tabs} />
      </div>
      {/* Reopened from the badge. Everything tenant-scoped on the screen behind is dropped and
          re-read by TenantCacheReset the moment the choice actually changes. */}
      <TenantSheet open={tenantSheetOpen} onClose={() => setTenantSheetOpen(false)} />
    </div>
  );
}
